/**
 * useFilteredNodes Hook
 *
 * Applies filter set to nodes and returns filtered results
 */

import { useMemo, useRef } from 'react';
import { GraphDataNode } from '../../types/graph.types';
import { FilterDefinition, FilterSet, FilterResult } from '../types';
import { executeFilterSet, generateFilterCacheKey } from '../engine/filterExecutor';

/**
 * Hook return type
 */
export interface UseFilteredNodesReturn {
  /** Filtered nodes */
  nodes: GraphDataNode[];
  /** Total count before filtering */
  totalCount: number;
  /** Count after filtering */
  filteredCount: number;
  /** Execution time in ms */
  executionTimeMs: number;
  /** Whether filters are active */
  isFiltering: boolean;
}

/**
 * Hook for filtering nodes based on active filters
 *
 * Uses memoization to prevent unnecessary re-computation
 *
 * @param allNodes - All nodes to filter
 * @param filterSet - Active filters
 * @param definitions - Filter definitions
 * @returns Filtered nodes and metadata
 */
export function useFilteredNodes(
  allNodes: GraphDataNode[],
  filterSet: FilterSet,
  definitions: Record<string, FilterDefinition>
): UseFilteredNodesReturn {
  /**
   * Last result, reused when neither the filters nor the nodes really changed.
   *
   * The guard exists because `filterSet` is rebuilt with a fresh identity on
   * renders where its CONTENT is unchanged, which would otherwise re-run the
   * whole filter pass over the catalog. `generateFilterCacheKey` is what sees
   * through that.
   *
   * The nodes are held BY REFERENCE, not by length. Length is not a proxy for
   * content: /bhajans stamps `meaningScope` onto its nodes when the meanings
   * artifact lands, producing a new array of the same length whose membership
   * answers are entirely different. Comparing lengths made that update
   * invisible — the list kept the pre-stamp answer (an empty list, since
   * nothing was stamped yet) for the rest of the session, and the only reason
   * a cold load worked was that the count happened to go 0 -> N. Any future
   * fact stamped onto nodes after first paint would have hit the same wall.
   */
  const cacheRef = useRef<{
    key: string;
    nodes: GraphDataNode[];
    result: FilterResult;
  } | null>(null);

  const result = useMemo(() => {
    const cacheKey = generateFilterCacheKey(filterSet);

    if (
      cacheRef.current &&
      cacheRef.current.key === cacheKey &&
      cacheRef.current.nodes === allNodes
    ) {
      return cacheRef.current.result;
    }

    const newResult = executeFilterSet(allNodes, filterSet, definitions);

    cacheRef.current = {
      key: cacheKey,
      nodes: allNodes,
      result: newResult,
    };

    return newResult;
  }, [allNodes, filterSet, definitions]);

  return useMemo(
    () => ({
      nodes: result.nodes,
      totalCount: result.totalCount,
      filteredCount: result.filteredCount,
      executionTimeMs: result.executionTimeMs,
      isFiltering: filterSet.length > 0,
    }),
    [result, filterSet.length]
  );
}
